'use client';

import { Icon } from '@/components/ui/icon';
import { IMediaItem } from '@/lib/types/media/core';
import { cn } from '@/lib/utils';
import { MediaType } from '@prisma/client';

export interface MediaPreviewProps {
  media: IMediaItem;
  onReplace?: (file: File) => Promise<void>;
  onRemove?: () => Promise<void>;
  onEdit?: () => void;
  className?: string;
  aspectRatio?: 'square' | 'video' | 'auto';
  showControls?: boolean;
}

export function MediaPreview({
  media,
  onReplace,
  onRemove,
  onEdit,
  className,
  aspectRatio = 'video',
  showControls = true
}: MediaPreviewProps) {
  const isVideo = media.type === MediaType.VIDEO;

  return (
    <div className={cn("relative group", className)}>
      <div className={cn(
        "w-full overflow-hidden rounded-lg",
        aspectRatio === 'square' && "aspect-square",
        aspectRatio === 'video' && "aspect-video"
      )}>
        {isVideo ? (
          <video
            src={media.url}
            controls
            className="w-full h-full object-cover"
          />
        ) : (
          <img
            src={media.url}
            alt={media.metadata?.alt || ''}
            className="w-full h-full object-cover"
          />
        )}
      </div>

      {showControls && (
        <div className={cn(
          "absolute bottom-2 left-2 flex items-center gap-3",
          "opacity-0 group-hover:opacity-100 transition-opacity",
          "touch:opacity-100 md:touch:opacity-0 md:touch:group-hover:opacity-100"
        )}>


          {onRemove && (
            <button
              onClick={onRemove}
              className="p-1 rounded-full bg-background/80  hover:bg-background/90 transition-colors"
            >
              <Icon icon="trash" className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* Hidden file input for replace */}
      {onReplace && (
        <input
          id={`file-input-${media.id}`}
          type="file"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onReplace(file);
          }}
          accept="image/*,video/*"
        />
      )}
    </div>
  );
}
